import { types, flow, castToSnapshot } from 'mobx-state-tree';
import { autorun, toJS } from 'mobx';
import axios from 'axios';

import Shipper from 'js/services/Shipper';
import ShippingDetail from 'js/stores/types/ShippingDetail';

function selectRate(predicate, locatedRate, rate) {
  if (predicate(rate) && (locatedRate === null || rate.price < locatedRate.price)) {
    return rate;
  }
  return locatedRate;
}

export default types
  .model('ShippingSelectorState', {
    value: types.maybeNull(ShippingDetail),
    options: types.optional(types.array(ShippingDetail), []),
    isLoading: false,
    lookupError: false,
    lookupCanceled: false,
    hasNoOptions: false,
  })
  .views(self => ({
    get hasShippingService() {
      return !!self.value;
    },
    get shippingService() {
      return self.value ? self.value.service : null;
    },
    get shippingOptions() {
      let options = toJS(self.options);
      return [
        options.reduce(function shortSameDayRateCallback(locatedRate, rate) {
          return selectRate(rate => rate.isSameDay && (rate.delivery === 'short' && rate.brand==='Gokada'), locatedRate, rate);
        }, null),
        options.reduce(function longSameDayRateCallback(locatedRate, rate) {
          return selectRate(rate => rate.isSameDay && (rate.delivery === 'long' && rate.brand==='Gokada'), locatedRate, rate);
        }, null),
        options.reduce(function longSameDayRateCallbackFez(locatedRate, rate) {
          return selectRate(rate => rate.isSameDay && (rate.delivery === 'long' && rate.brand==='Fez'), locatedRate, rate);
        }, null),
        options.reduce(function standardRateCallback(locatedRate, rate) {
          return selectRate(rate => !rate.isSameDay && rate.brand, locatedRate, rate);
        }, null),
      ].filter(x => x);
    },
  }))
  .actions(self => {
    let shipper = null;
    let alias, size, destination, onChange;
    const disposers = [];
    return {
      afterCreate() {
        shipper = new Shipper();
      },
      beforeDestroy() {
        disposers.forEach(dispose => dispose());
      },

      initialize(options) {
        alias = options.alias;
        size = options.size;
        destination = options.destination;
        onChange = options.onChange;

        disposers.forEach(dispose => dispose());
        disposers.push(
          autorun(() => {
            if (
              alias &&
              destination.hasAddress &&
              (typeof options.isReady === 'undefined' || options.isReady) &&
              !self.hasNoOptions &&
              !self.lookupError &&
              !self.isLoading &&
              !self.options.length
            ) {
              self.load();
            } else if (!destination.hasAddress) {
              self.resetSelector();
            }
          }),
          autorun(() => {
            if (!options.isReady) {
              shipper.cancelRatesRequest();
              self.resetSelector();
            }
          }),
        );

        return self;
      },

      resetSelector() {
        self.options = [];
        self.lookupError = false;
        self.hasNoOptions = false;
        self.value = null;
      },

      load: flow(function* load() {
        if (alias && destination && destination.hasAddress) {
          self.isLoading = true;
          self.lookupError = false;
          self.lookupCanceled = false;
          self.options = [];

          try {
            self.options = yield shipper.getShippingOptions(alias, destination, size);
          } catch (error) {
            if (axios.isCancel(error)) {
              self.lookupCanceled = true;
            } else {
              // self.lookupError = true;
              // console.error(error);
            }
          }

          localStorage.setItem('shipping_options', JSON.stringify(self.options));
          if (self.shippingOptions.length) {
            self.setValue(self.shippingOptions[0]);
          }
        }

        self.isLoading = false;
        self.hasNoOptions = !self.lookupError && !self.lookupCanceled && !self.shippingOptions.length;

        return true;
      }),

      setValue(value) {
        value = castToSnapshot(value);
        self.value = value;
        onChange(value);
        return self;
      },
    };
  });
